import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { 
  HiOutlineUser,
  HiOutlineLockClosed, 
  HiOutlineEye, 
  HiOutlineEyeOff,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle
} from 'react-icons/hi';
import { useAuth } from '@/context/AuthContext';
import { 
  useFormValidation, 
  validators, 
  getPasswordStrength 
} from '@/hooks/useFormValidation';
import ThemeToggle from '@/components/common/ThemeToggle';
import type { ValidationSchema } from '@/types';
import logo from './../assets/logo.svg';

interface LoginFormValues {
  username: string;
  password: string;
  remember: string;
}

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { login, isAuthenticated, error: authError, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string>('');

  const validationSchema: ValidationSchema = {
    username: [
      validators.required('El usuario es requerido'),
    ],
    password: [
      validators.required('La contraseña es requerida'),
      validators.minLength(6, 'La contraseña debe tener al menos 6 caracteres')
    ]
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    getFieldState,
    isSubmitting
  } = useFormValidation<LoginFormValues>(
    { username: '', password: '', remember: 'false' },
    validationSchema
  );

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (formValues: LoginFormValues): Promise<void> => {
    setLoginError('');
    clearError();
    
    const result = await login(formValues.username, formValues.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setLoginError(result.error ?? 'Error al iniciar sesión');
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(prev => !prev);
  };

  const usernameState = getFieldState('username');
  const passwordState = getFieldState('password');
  const passwordStrength = getPasswordStrength(values.password);

  const getStrengthColor = (): string => {
    switch (passwordStrength.color) {
      case 'strong':
        return 'var(--secondary)';
      case 'medium':
        return '#f59e0b';
      case 'weak':
        return '#ef4444';
      default:
        return 'var(--text-muted)';
    }
  };

  return (
    <div className="login-page">
      <ThemeToggle />
      
      <div className="login-container fade-in">
        <div className="login-card">
          <div className="login-header text-center">
            <img src={logo} className='img-fluid mb-3' alt="Logo" />
            <h1 className="login-title">Bienvenido</h1>
            <p className="login-subtitle">Ingresa tus credenciales para acceder al panel</p>
          </div>

          {(loginError || authError) && (
            <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 fade-in" role="alert">
              <HiOutlineExclamationCircle size={20} />
              <span>{loginError || authError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                Usuario
              </label>
              <div className="input-group">
                <span className="input-icon">
                  <HiOutlineUser />
                </span>
                <input
                  type="text"
                  id="username"
                  name="username"
                  className={`form-control has-icon-left ${
                    usernameState.isValid ? 'is-valid' : ''
                  } ${usernameState.isInvalid ? 'is-invalid' : ''}`}
                  placeholder="Usuario"
                  value={values.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="username"
                  disabled={isSubmitting}
                />
                {usernameState.isValid && (
                  <span className="input-icon input-icon-right" style={{ color: 'var(--secondary)' }}>
                    <HiOutlineCheckCircle />
                  </span>
                )}
              </div>
              {usernameState.isInvalid && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  {usernameState.error}
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Contraseña
              </label>
              <div className="input-group" style={{ position: 'relative' }}>
                <span className="input-icon">
                  <HiOutlineLockClosed />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className={`form-control has-icon-left has-icon-right ${
                    passwordState.isValid ? 'is-valid' : ''
                  } ${passwordState.isInvalid ? 'is-invalid' : ''}`}
                  placeholder="••••••••"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  style={{ paddingRight: '45px' }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle-btn"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    zIndex: 10,
                  }}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <HiOutlineEyeOff size={20} />
                  ) : (
                    <HiOutlineEye size={20} />
                  )}
                </button>
              </div>
              
              {values.password && (
                <div className="fade-in">
                  <div className="password-strength">
                    <div className={`password-strength-bar ${passwordStrength.score >= 1 ? passwordStrength.color : ''}`} />
                    <div className={`password-strength-bar ${passwordStrength.score >= 2 ? passwordStrength.color : ''}`} />
                    <div className={`password-strength-bar ${passwordStrength.score >= 3 ? passwordStrength.color : ''}`} />
                  </div>
                  <p className="form-text" style={{ color: getStrengthColor() }}>
                    Seguridad: {passwordStrength.label}
                  </p>
                </div>
              )}
              
              {passwordState.isInvalid && (
                <p className="form-text error fade-in">
                  <HiOutlineExclamationCircle style={{ marginRight: 4 }} />
                  {passwordState.error}
                </p>
              )}
            </div>

            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  className="form-check-input"
                  checked={values.remember === 'true'}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="remember" className="form-check-label">
                  Recordarme
                </label>
              </div>
              <a href="/forgot-password" className="text-primary" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                ¿Olvidaste tu contraseña?
              </a>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg btn-block"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}

export default LoginPage;